import React, { memo, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutRectangle, StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import CheckmarkIcon from '@/assets/icons/checkmark';
import ClockIcon from '@/assets/icons/clock';
import { Button, ButtonProps } from '@/components/ButtonComponent';
import { SizedBox } from '@/components/SizedBox';
import { Text } from '@/components/TextComponent';
import { Box, Image, Row, TouchableBox } from '@/components/lib/components';
import { user } from '@/hooks/use-user';
import { common } from '@/styles/styles';
import { PromotionThemeDocument } from '@/types/prismic.generated';
import { isIos } from '@/utils/constants-platform-specific';
import { formatPromoExpirationTime } from '@/utils/renderDate';

import { usePromoTheme } from '../hooks/use-promo-theme';
import { usePromoOptIn } from '../hooks/use-submissions';
import { OvalGlow } from './OvalGlow';

const BOTTOM_GLOW_HEIGHT = 120;
const TOP_GLOW_HEIGHT = 180;

type Props = {
    promoId: string;
    segmented: boolean;
    title: string;
    secondaryButton?: string;
    expirationTime?: string;
    hasOptedIn: boolean;
    showOptInButton: boolean;
    imageUri: string;
    redirectPath?: string;
    dynamicHeight?: boolean;
    theme: PromotionThemeDocument['data'];
    onLocalOptIn?: (promoId: string) => void;
    onPress: () => void;
};

const CARD_CONFIG = {
    dynamic: {
        imageSize: { width: 120, height: 120 },
        imageSeparator: 12,
        titleSeparator: 20,
        titleNumberOfLines: undefined,
        titleFontSize: 20,
        titleLetterSpacing: -0.8,
        shouldShowExpiration: true,
        titleLineHeight: 24,
        buttonSize: 's' as ButtonProps['size'],
        buttonHorizontalPadding: 14,
        buttonVerticalPadding: 8,
        optedInButtonBorderWidth: 2,
        iconSize: 20,
    },
    fixed: {
        imageSize: { width: 88, height: 120 },
        imageSeparator: 8,
        titleSeparator: 16,
        titleNumberOfLines: 2,
        titleFontSize: 18,
        titleLetterSpacing: -0.54,
        shouldShowExpiration: false,
        titleLineHeight: 22,
        buttonSize: 'xs' as ButtonProps['size'],
        buttonHorizontalPadding: 10,
        buttonVerticalPadding: 4,
        optedInButtonBorderWidth: 1.5,
        iconSize: 16,
    },
} as const;

const getCardSizingConfig = (dynamicHeight?: boolean) => {
    return dynamicHeight ? CARD_CONFIG.dynamic : CARD_CONFIG.fixed;
};

export const PromoCard = memo(
    ({
        promoId,
        segmented,
        title,
        secondaryButton,
        expirationTime,
        hasOptedIn,
        showOptInButton,
        imageUri,
        redirectPath,
        dynamicHeight,
        theme,
        onLocalOptIn,
        onPress,
    }: Props) => {
        const [cardSize, setCardSize] = useState<LayoutRectangle>({ width: 0, height: 0, x: 0, y: 0 });
        const {
            imageSize,
            imageSeparator,
            titleSeparator,
            titleNumberOfLines,
            titleFontSize,
            titleLetterSpacing,
            shouldShowExpiration,
            titleLineHeight,
            buttonSize,
            buttonHorizontalPadding,
            buttonVerticalPadding,
            optedInButtonBorderWidth,
            iconSize,
        } = getCardSizingConfig(dynamicHeight);
        const { t } = useTranslation(['promohub']);
        const navigation = useNavigation();
        const { optIn, loading } = usePromoOptIn(promoId, segmented, redirectPath, () => {
            onLocalOptIn?.(promoId);
        });

        const {
            background_color,
            promo_title_color,
            bottom_glow_color,
            top_glow_color,
            expiration_tag_color,
            primary_button_background_color,
            primary_button_text_color,
            secondary_button_text_color,
        } = usePromoTheme(theme);
        const paddingHorizontal = showOptInButton ? buttonHorizontalPadding : 0;

        const onCardPress = useCallback(() => {
            if (user.isGuest()) {
                navigation.reset({ routes: [{ name: 'Launch' }] });
                return;
            }
            onPress();
        }, [navigation, onPress]);

        return (
            <TouchableBox
                flex={1}
                flexDirection={'row'}
                onPress={onCardPress}
                activeOpacity={0.7}
                style={{ backgroundColor: background_color, ...common.borderCurve }}
                borderRadius={'r20'}
                overflow={'hidden'}
                onLayout={event => setCardSize(event.nativeEvent.layout)}
            >
                <OvalGlow
                    x={-48}
                    y={-88}
                    width={cardSize.width}
                    height={TOP_GLOW_HEIGHT}
                    color={top_glow_color}
                    opacity={0.4}
                />
                <Box paddingVertical={'s16'} pl={'s16'} flex={1}>
                    <Box flex={1} justifyContent={'space-between'}>
                        <Text
                            style={[styles.title, { color: promo_title_color }]}
                            fontSize={titleFontSize}
                            lineHeight={titleLineHeight}
                            letterSpacing={titleLetterSpacing}
                            numberOfLines={titleNumberOfLines}
                        >
                            {title}
                        </Text>
                        {expirationTime && shouldShowExpiration ? (
                            <Row alignItems={'center'} gap={'s4'} mt={'s8'}>
                                <ClockIcon color={expiration_tag_color} />
                                <Box flex={1}>
                                    <Text
                                        style={{ color: expiration_tag_color }}
                                        numberOfLines={1}
                                        ellipsizeMode={'tail'}
                                        variant={'labelLarge'}
                                    >
                                        {formatPromoExpirationTime(expirationTime, t)}
                                    </Text>
                                </Box>
                            </Row>
                        ) : null}
                        <SizedBox value={titleSeparator} />
                        <Row gap={showOptInButton ? 's4' : 's0'}>
                            {hasOptedIn ? (
                                <Button
                                    label={
                                        <Row gap={dynamicHeight ? 's8' : 's4'} alignItems={'center'}>
                                            <Text variant={'titleSmall'} style={{ color: secondary_button_text_color }}>
                                                {t('opted_in')}
                                            </Text>
                                            <CheckmarkIcon
                                                width={iconSize}
                                                height={iconSize}
                                                color={primary_button_background_color}
                                            />
                                        </Row>
                                    }
                                    size={buttonSize}
                                    hierarchy={'tertiary'}
                                    shape={'rect'}
                                    style={{
                                        borderColor: primary_button_background_color,
                                        borderWidth: optedInButtonBorderWidth,
                                        paddingHorizontal: buttonHorizontalPadding - optedInButtonBorderWidth,
                                        paddingVertical: buttonVerticalPadding - optedInButtonBorderWidth,
                                        ...styles.button,
                                    }}
                                    disabled={true}
                                />
                            ) : (
                                <Button
                                    label={
                                        <Text
                                            variant={buttonSize === 's' ? 'titleSmall' : 'bodySmaller'}
                                            style={{ color: primary_button_text_color }}
                                            fontWeight={'600'}
                                            letterSpacing={buttonSize === 's' ? -0.08 : -0.23}
                                        >
                                            {showOptInButton ? t('opt_in') : secondaryButton ?? t('learn_more')}
                                        </Text>
                                    }
                                    variant={'dark'}
                                    hitSlop={8}
                                    size={buttonSize}
                                    shape={'rect'}
                                    style={{
                                        paddingHorizontal: buttonHorizontalPadding,
                                        paddingVertical: buttonVerticalPadding,
                                        backgroundColor: primary_button_background_color,
                                        ...styles.button,
                                    }}
                                    disabled={false}
                                    loading={loading}
                                    onPress={showOptInButton ? optIn : onCardPress}
                                />
                            )}
                            {showOptInButton ? (
                                <Button
                                    label={
                                        <Text
                                            variant={buttonSize === 's' ? 'titleSmall' : 'bodySmaller'}
                                            style={{ color: secondary_button_text_color }}
                                            fontWeight={dynamicHeight ? '600' : '700'}
                                        >
                                            {secondaryButton ?? t('learn_more')}
                                        </Text>
                                    }
                                    onPress={onCardPress}
                                    size={buttonSize}
                                    shape={'rect'}
                                    style={[
                                        common.backgroundColorTransparent,
                                        {
                                            //if there's only one button, remove the padding so it's properly aligned to the left
                                            paddingHorizontal,
                                            paddingVertical: buttonVerticalPadding,
                                        },
                                    ]}
                                    hierarchy={'tertiary'}
                                />
                            ) : null}
                        </Row>
                    </Box>
                </Box>
                <SizedBox value={imageSeparator} />
                <Image source={{ uri: imageUri }} resizeMode={'contain'} style={[styles.image, { ...imageSize }]} />
                <OvalGlow
                    x={0}
                    y={cardSize.height - 34}
                    width={cardSize.width}
                    height={BOTTOM_GLOW_HEIGHT}
                    color={bottom_glow_color}
                    opacity={0.3}
                />
            </TouchableBox>
        );
    }
);

const styles = StyleSheet.create({
    image: {
        marginRight: -12,
        alignSelf: 'center',
    },
    title: {
        fontFamily: 'gamay-extrabold',
        textTransform: 'uppercase',
        // on android, the custom font breaks if we add a fontWeight
        fontWeight: isIos ? '800' : undefined,
    },
    button: {
        borderCurve: 'continuous',
    },
});
